/* Pages some classes add to their set, drawn at the official sheets' measured
   positions: the Druid's Beastform list, the Beastbound Ranger's companion sheet
   and the Martial Artist's stances sheet. */

import { FileText, Scissors } from 'lucide-react';
import type { MartialFormData } from './content/classes';
import { DomainIcon } from './cards/DomainIcon';
import { classDomains, classFeatures } from './catalog';
import { COMPANION_SHEET } from './content/sheetText';
import { Character, markCount, Track as TrackName } from './model';
import { canLearnStance, forgetStance, knownStances, learnStance, tierOf } from './leveling';
import { companionStats } from './companion';
import { emphasize, PageProps } from './SheetParts';
import { Banner, cutBox, HeadTab, Html, PageFooter, Pill, SheetSvg, Slot, TileStripes, Track, Txt, Value, WriteLine } from './svg';
import { ClassFeatureBlock } from './SheetPage';

type Beastform = PageProps['catalog']['beastforms'][number];

interface EntryProps {
  form: Beastform;
  /** Set when the Druid can take this form now. */
  onPick?: () => void;
  current?: boolean;
}

function BeastformEntry({ form, onPick, current }: EntryProps) {
  const attack = `${form.attackDamage}${form.attackMod ? `+${form.attackMod}` : ''} phy`;
  return (
    <div className={`cs-beast${current ? ' is-current' : ''}`}>
      <h4 className="cs-beast__name">{form.name}</h4>
      {current && <span className="cs-beast__go">Your form</span>}
      {!current && onPick && (
        <button type="button" className="cs-beast__go" aria-label={`Transform into ${form.name}`} onClick={onPick}>Transform</button>
      )}
      {/* Legendary and Mythic Beast upgrade another form, so they have no stats of their own. */}
      <p className="cs-beast__examples">({form.examples}{form.trait ? ', etc.' : ''})</p>
      {form.trait && <p className="cs-beast__stats">{form.trait} +{form.traitMod} | Evasion +{form.evasionMod}</p>}
      {form.attackDamage && <p className="cs-beast__stats">{form.attackRange} {form.attackTrait} {attack}</p>}
      {form.advantages.length > 0 && <p><strong>Gain advantage on:</strong> {form.advantages.join(', ')}</p>}
      {form.features.map((f) => (
        <p key={f.name}><em className="cs-feature-name">{f.name}:</em> {emphasize(f.text)}</p>
      ))}
    </div>
  );
}

/** Three columns filled left to right, each stacking its forms, as the printed list does. */
function BeastColumns({ forms, entry }: { forms: Beastform[]; entry: (f: Beastform) => EntryProps }) {
  return (
    <>
      {[0, 1, 2].map((col) => (
        <div key={col} className="cs-beasts__col">
          {forms.filter((_, i) => i % 3 === col).map((f) => <BeastformEntry key={f.name} {...entry(f)} />)}
        </div>
      ))}
    </>
  );
}

export function BeastformPages({ catalog, c, stats, onBeastform }: PageProps) {
  const cls = catalog.cls(c.className);
  const forms = (tier: number) => catalog.beastforms.filter((f) => f.tier === tier);
  // "A creature of your tier or lower from the Beastform list"
  const entry = (form: Beastform): EntryProps => ({
    form,
    current: c.beastform?.name === form.name,
    onPick: onBeastform && form.tier <= tierOf(stats.level) ? () => onBeastform(form.name) : undefined,
  });
  return (
    <>
      {[[1, 2], [3, 4]].map(([upper, lower], page) => (
        <SheetSvg key={page} label={page ? 'Beastform, continued' : 'Beastform'} className="cs-extra">
          <path className="s-band" d="M0 0H248.4L214.4 54.7H0Z" />
          <Txt x={16.7} y={29.4} size={19} weight={900} tone="band">{(cls?.name ?? 'Druid').toUpperCase()}</Txt>
          <Txt x={16} y={42.4} size={11} weight={300} tone="band">BEASTFORM</Txt>
          <rect className="s-tint2" x={248.5} y={38.2} width={327.5} height={17} />
          <Txt x={251.3} y={51.4} size={12} weight={300}>TIER {upper}</Txt>
          <Html x={36} y={68} w={540} h={284} className="cs-beasts" fit>
            <BeastColumns forms={forms(upper)} entry={entry} />
          </Html>
          <rect className="s-tint2" x={36} y={356.8} width={540} height={17} />
          <Txt x={39.3} y={370} size={12} weight={300}>TIER {lower}</Txt>
          <Html x={36} y={386} w={540} h={384} className="cs-beasts" fit>
            <BeastColumns forms={forms(lower)} entry={entry} />
          </Html>
          <PageFooter hf={cls?.source === 'HF'} x={25.9} />
        </SheetSvg>
      ))}
    </>
  );
}

export function CompanionPage({ c, onMark }: PageProps) {
  const rows = [360.9, 380.6, 400.2, 419.9, 439.6];
  const stats = companionStats(c);
  return (
    <SheetSvg label="Companion" className="cs-extra">
      <TileStripes w={151} />
      <path className="s-band" d="M151 0H347L313 54.7H151Z" />
      <Txt x={168.8} y={29.4} size={19} weight={900} tone="band">RANGER</Txt>
      <Txt x={168} y={42.4} size={11} weight={300} tone="band">COMPANION</Txt>

      {/* Evasion */}
      <g transform="translate(480.6 -59.1)">
        <path className="s-frame" d="M16 96.8L27 85.5L38 90V110.1H16Z" />
        <path className="s-frame" d="M62.8 96.8L51.8 85.5L40.8 90V110.1H62.8Z" />
        <path className="s-paper o-frame" d="M39.4 97.2L19.7 116.9L33.2 130.3H45.6L59.3 116.9Z" />
        <path className="s-paper o-frame" d="M18.7 97.6C23 88 30 79.5 39.6 77.4C49.2 79.5 56.2 88 60.2 97.5V99.7L62.7 102.9V124.3H16.1V103.1L18.7 100Z" />
        <path className="o-faint" d="M22.6 97.6C26.6 89 32.6 82.5 39.6 80.8C46.6 82.5 52.6 89 56.3 97.5V101.2L59.3 104.6V120.9H19.5V104.4L22.6 100.9Z" />
        <Txt x={39.4} y={107} size={18} weight={800} anchor="middle">{stats.evasion}</Txt>
      </g>
      <Pill x={494.2} y={57.8} w={51.6} h={9.6} label="EVASION" size={7.7} />
      <Txt x={520} y={86} size={8} weight={500} anchor="middle">Start at 10</Txt>

      {/* Name and picture */}
      <WriteLine x0={178.2} x1={474.6} y={76.5} />
      <Value x={186} base={78} w={280}>{c.companionName}</Value>
      <Txt x={186} y={91.1} size={6} weight={700}>COMPANION NAME</Txt>
      <path className="o-faint" d={cutBox(178.6, 100.2, 550.5, 253.3, 7)} />
      <path className="o-faint" d="M182.1 152.5V109L186.5 103.8H230.6M498.5 105.5H542.6L547 110.7V154.3M182.1 201V245.3L186.5 249.7H230.6M498.5 249.7H542.6L547 245.3V201" />
      <Html x={190} y={168} w={350} h={20} size={11} className="cs-center">{c.companionAnimal}</Html>
      <Html x={168} y={262.5} w={395} h={44} size={8} className="cs-companion-text" fit>{COMPANION_SHEET.intro}</Html>

      {/* Companion Experience */}
      <Txt x={170.8} y={319.1} size={9} weight={900} tone="muted">COMPANION EXPERIENCE</Txt>
      <Html x={170.3} y={321.3} w={252} h={22} size={8} className="cs-companion-text" fit>{COMPANION_SHEET.experience}</Html>
      {rows.map((y, i) => {
        const exp = stats.experiences[i];
        return (
          <g key={y}>
            <path className="o-faint" d={`M171.7 ${y + 2.2}L174.2 ${y + 5.3}H369.3`} />
            <path className="s-tint" d={`M410.7 ${y}L406.9 ${y + 3.8}H384.3Q382.4 ${y} 380.5 ${y}H377.1L371.5 ${y + 5.7}H414.4V${y}Z`} />
            <Value x={175} base={y + 4.4} w={192}>{exp?.name}</Value>
            {exp?.name && <Txt x={394} y={y + 2.4} size={8.5} weight={900} anchor="middle">+{exp.bonus}</Txt>}
          </g>
        );
      })}
      <Txt x={436.4} y={328.5} size={8} weight={900}>Example Companion Experiences</Txt>
      <Html x={436.4} y={336.3} w={128} h={100} size={8} className="cs-companion-text">{COMPANION_SHEET.examples.join(', ')}</Html>
      <Html x={173.2} y={457.3} w={392} h={32} size={8} className="cs-companion-text" fit>{emphasize(COMPANION_SHEET.command)}</Html>

      {/* Attack & Damage; this frame and Stress's start at the stripes, which hide their left side on the printed sheet. */}
      <path className="o-frame" d="M151 506.9H335.2L340.8 512.5V621.8L335.2 627.4H151" />
      <Banner x0={180.9} x1={310.1} y={499.6} h={13.7} title="ATTACK & DAMAGE" tab={[207.7, 283.3]} tabRaise={2.2} wings={[175, 316]} />
      <line className="o-rule o-dots" x1={239.6} y1={527.2} x2={239.6} y2={534.4} />
      <WriteLine x0={160.3} x1={333.4} y={535.6} />
      <Value x={244} base={537} w={80} size={8}>{stats.range}</Value>
      <Txt x={167.1} y={548.4} size={8} weight={900}>Standard Attack</Txt>
      <Txt x={248.6} y={548.4} size={8} weight={900}>Range</Txt>
      {['d6', 'd8', 'd10', 'd12'].map((d, i) => (
        <g key={d}>
          <circle className={`s-paper ${d === stats.damage ? 'o-ink' : 'o-rule'}`} cx={175.9 + i * 30.5} cy={558.9} r={3} />
          <Txt x={181 + i * 30.5} y={561.3} size={8} weight={900}>{d}</Txt>
        </g>
      ))}
      <Txt x={291.5} y={561.3} size={8} weight={900}>phy / mag</Txt>
      <Html x={164.3} y={567.6} w={174} h={56} size={8} className="cs-companion-text">{COMPANION_SHEET.attack}</Html>

      {/* Stress */}
      <path className="o-frame" d="M151 630.8H335.2L340.8 636.4V813.5L335.2 819.1H151" />
      <Banner x0={180.9} x1={310.1} y={622.1} h={13.7} title="STRESS" tab={[207.7, 283.3]} tabRaise={2.1} wings={[175, 316]} />
      <Txt x={168.5} y={663.6} size={8} weight={900} tone="muted">STRESS:</Txt>
      <Track
        label="Companion Stress"
        total={6}
        open={stats.stress}
        marked={markCount(c, 'companionStress')}
        onSet={onMark && ((n) => onMark('companionStress', n))}
        slot={(i) => <rect className="shape" x={208 + i * 20.2} y={656.2} width={18.3} height={7} rx={1.2} />}
      />
      <Html x={169.5} y={671.2} w={168} h={140} size={8} className="cs-companion-text">
        {COMPANION_SHEET.stress.map((p) => <p key={p}>{p}</p>)}
      </Html>

      {/* Training */}
      <path className="o-frame" d={cutBox(344, 507.4, 640, 815.4, 5.6)} />
      <Banner x0={397.9} x1={527.1} y={500.6} h={13.7} title="TRAINING" tab={[424.7, 500.3]} tabRaise={2.2} wings={[392, 533]} />
      <Html x={357.3} y={521.6} w={210} h={22} size={8} className="cs-companion-text" fit>{COMPANION_SHEET.training}</Html>
      <Html x={350} y={548} w={250} h={262} size={8} className="cs-training">
        {COMPANION_SHEET.options.map((o) => (
          <div key={o.name} className="cs-training__row">
            <span className="cs-training__boxes">
              {Array.from({ length: o.boxes }, (_, i) =>
                i < (stats.training[o.name] ?? 0)
                  ? <span key={i} className="cs-box is-marked" role="img" aria-label={`${o.name} marked`} />
                  : <span key={i} className="cs-box" />,
              )}
            </span>
            <span>
              <em className="cs-feature-name">{o.name}:</em>{' '}
              {o.name === 'Light in the Dark' ? <span className="cs-hope-slot"><span className="cs-hope-slot__mark" />{o.text}</span> : emphasize(o.text)}
            </span>
          </div>
        ))}
      </Html>
      <PageFooter x={25.9} />
    </SheetSvg>
  );
}

type StancesProps = PageProps & { subclass: string; form: MartialFormData };
type SetCharacter = (patch: Partial<Character>) => void;

/** The Martial Stances page, as the character chose it: a half page to cut out, or a full page. */
export function StancesPage(props: StancesProps) {
  return props.c.stanceSheet === 'full' ? <FullStancesPage {...props} /> : <HalfStancesPage {...props} />;
}

/** On screen above the Martial Stances page: half or full page, and how each prints. */
export function StanceSheetChoice({ c, onSet }: { c: Character; onSet: SetCharacter }) {
  const full = c.stanceSheet === 'full';
  return (
    <div className="cs-page-choice" role="group" aria-label="Martial Stances page">
      <div className="cb-switch">
        <button type="button" className="back-btn" aria-pressed={!full} onClick={() => onSet({ stanceSheet: 'half' })}>
          <Scissors size={15} aria-hidden="true" />Half page
        </button>
        <button type="button" className="back-btn" aria-pressed={full} onClick={() => onSet({ stanceSheet: 'full' })}>
          <FileText size={15} aria-hidden="true" />Full page
        </button>
      </div>
      <p className="cc-note">
        {full
          ? 'Martial Stances prints as a page of its own, with a line for each stance you know.'
          : 'Martial Stances prints on half the page, beside a striped margin: cut it out as a side sheet to keep by your character sheet.'}
      </p>
    </div>
  );
}

/** A stance's Known ring: on screen it learns or forgets the stance, within the Stance Fighter limit. */
function StanceRing({ c, name, tier, onSet }: { c: Character; name: string; tier: number; onSet?: SetCharacter }) {
  const known = knownStances(c).includes(name);
  const className = `cs-stance__ring${known ? ' is-marked' : ''}`;
  if (!onSet) return <span className={className} />;
  return (
    <button
      type="button"
      className={className}
      aria-label={`Know ${name}`}
      aria-pressed={known}
      disabled={!known && !canLearnStance(c, tier)}
      title={known ? 'Known' : 'Learn a stance each level up, from your tier or lower'}
      onClick={() => onSet(known ? forgetStance(c, name) : learnStance(c, name, tier))}
    />
  );
}

/** The Focus track: six diamonds on a bar, marked as Focus is gained. */
function FocusTrack({ c, onMark, dx = 0, dy = 0 }: { c: Character; onMark?: (track: TrackName, count: number) => void; dx?: number; dy?: number }) {
  return (
    <g transform={`translate(${dx} ${dy})`}>
      <Txt x={334.3} y={182.7} size={12} weight={900} tone="title">FOCUS:</Txt>
      <path className="s-hopebar" d="M396.9 171.3H568.8L575.3 177.8V179.2L568.8 185.7H396.9L390.4 179.2V177.8Z" />
      {[0, 1, 2, 3, 4].map((k) => <line key={k} className="o-paper" x1={422.1 + k * 30.38} y1={175.3} x2={422.1 + k * 30.38} y2={181.7} />)}
      <Track label="Focus" total={6} marked={markCount(c, 'focus')} onSet={onMark && ((n) => onMark('focus', n))}
        slot={(k) => <rect className="shape shape--diamond" x={402.7 + k * 30.38} y={174.2} width={8.6} height={8.6} rx={1} transform={`rotate(45 ${407 + k * 30.38} 178.5)`} />} />
    </g>
  );
}

function HalfStancesPage({ c, subclass, form, onMark, onSet }: StancesProps) {
  return (
    <SheetSvg label="Martial Stances" className="cs-extra">
      <TileStripes w={301} />
      <HeadTab x={302} w={224.3} h={60.8} title={subclass} subtitle={form.title ?? 'Martial Stances'} textX={19.4} />
      <path className="s-tint2 o-rule" d={cutBox(328.5, 72.5, 584, 160.2, 6)} />
      <Html x={337.5} y={81.5} w={240} h={76} size={8} className="cs-stances-text" fit>{emphasize(form.description)}</Html>
      <FocusTrack c={c} onMark={onMark} />
      <Html x={317.3} y={195.9} w={279.2} h={580} className="cs-stance-list" fit>
        {Object.entries(form.tiers).map(([tier, stances]) => (
          <section key={tier} className="cs-stance-tier">
            <h4 className="cs-stance-tier__band"><span className="cs-stance-tier__known">Known</span> Tier {tier}</h4>
            {stances.map((s) => (
              <p key={s.name} className="cs-stance">
                <StanceRing c={c} name={s.name} tier={Number(tier)} onSet={onSet} />
                <span><em className="cs-feature-name">{s.name}:</em> {emphasize(s.description)}</span>
              </p>
            ))}
          </section>
        ))}
      </Html>
      <PageFooter hf x={25.9} />
    </SheetSvg>
  );
}

const STANCE_TIERS = ['1', '2', '3', '4'];

/** One known stance's line: its Active circle (one stance at a time), its name, and the Stance label under it. */
function StanceLine({ c, y, name, onSet }: { c: Character; y: number; name: string; onSet?: SetCharacter }) {
  const ring = <circle className="shape" cx={46.65} cy={y - 4.2} r={5.55} />;
  return (
    <g>
      {name ? (
        <Slot label={`${name} active`} marked={c.activeStance === name} onToggle={onSet && (() => onSet({ activeStance: c.activeStance === name ? '' : name }))}>
          {ring}
        </Slot>
      ) : (
        <g className="cs-slot">{ring}</g>
      )}
      <Txt x={46.4} y={y + 7.1} size={4.6} weight={900} anchor="middle">ACTIVE</Txt>
      <WriteLine x0={60} x1={584.6} y={y - 3.2} />
      {name && <Txt x={72} y={y - 3.4} size={9.5} weight={700}>{name}</Txt>}
      <Txt x={72} y={y + 8.4} size={6.4} weight={800}>Stance</Txt>
    </g>
  );
}

/**
 * The full-page Martial Stances sheet, after the official Martial Form page: a line per known stance under each
 * tier band (two at least, to write in), then every stance by tier in two columns.
 */
function FullStancesPage({ c, subclass, form, onMark, onSet }: StancesProps) {
  const lines = STANCE_TIERS.map((t) => {
    const known = (form.tiers[t] ?? []).filter((s) => knownStances(c).includes(s.name)).map((s) => s.name);
    return [...known, ...Array<string>(Math.max(0, 2 - known.length)).fill('')];
  });
  const count = lines.reduce((n, l) => n + l.length, 0);
  // Measured from the official sheet: 18pt tier bands, a line 22pt under each band, then 34.6pt apart. With more
  // known stances than two per tier, the lines close up to keep the stance list on the page.
  const lineGap = Math.min(34.6, (430 - 248) / (count - 4));
  let y = 78;
  const tiers = lines.map((names, i) => {
    const top = y;
    y += 62 + (names.length - 1) * lineGap;
    return { tier: STANCE_TIERS[i], top, names };
  });
  const listTop = y + 6;
  const column = (from: number) => STANCE_TIERS.slice(from, from + 2).map((tier) => (
    <section key={tier} className="cs-stance-group">
      <h4 className="cs-stance-group__title">Tier {tier} stances</h4>
      {(form.tiers[tier] ?? []).map((s) => (
        <p key={s.name} className="cs-stance">
          <StanceRing c={c} name={s.name} tier={Number(tier)} onSet={onSet} />
          <span><em className="cs-feature-name">{s.name}:</em> {emphasize(s.description)}</span>
        </p>
      ))}
    </section>
  ));
  return (
    <SheetSvg label="Martial Stances" className="cs-extra">
      <HeadTab w={246.5} h={48.8} slant={20.5} title={subclass} subtitle={form.title ?? 'Martial Stances'} />
      <Html x={262} y={6} w={334} h={44} size={7.2} className="cs-stances-text" fit>{emphasize(form.description)}</Html>
      <FocusTrack c={c} onMark={onMark} dx={14} dy={-116.5} />
      {tiers.map(({ tier, top, names }) => (
        <g key={tier}>
          <rect className="s-tint" x={26.5} y={top} width={563.4} height={18} />
          <Txt x={36} y={top + 13.6} size={13} weight={300} tone="soft">TIER {tier}</Txt>
          {names.map((name, k) => <StanceLine key={name || k} c={c} y={top + 40 + k * lineGap} name={name} onSet={onSet} />)}
        </g>
      ))}
      <Html x={30} y={listTop} w={272} h={774 - listTop} className="cs-stance-list cs-stance-list--full" fit>{column(0)}</Html>
      <Html x={314} y={listTop} w={276} h={774 - listTop} className="cs-stance-list cs-stance-list--full" fit>{column(2)}</Html>
      <PageFooter hf />
    </SheetSvg>
  );
}

/** The multiclass module, laid beside the sheet: the second class's domains and class feature. */
export function MulticlassPage({ c, catalog, onChoose }: PageProps) {
  const multi = c.multiclass;
  const cls = multi && catalog.cls(multi.className);
  if (!multi || !cls) return null;
  const domains = classDomains(cls);
  return (
    <SheetSvg label="Multiclass" className="cs-extra">
      <TileStripes w={325} />
      <rect className="s-paper" x={40.2} y={371.7} width={240.9} height={29.7} />
      <Txt x={160.6} y={393.1} size={16} weight={900} tone="title" anchor="middle">CHARACTER SHEET</Txt>
      {[99.3, 372, 660.2].map((y) => <path key={y} className="s-tint" d={`M310.3 ${y}L321.3 ${y + 6.3}L310.3 ${y + 12.6}Z`} />)}
      <path className="s-band" d="M324.8 0H534.2L500 50.5H324.8Z" />
      <Txt x={335.9} y={25.8} size={19} weight={900} tone="band">{cls.name.toUpperCase()}</Txt>
      <Txt x={335.9} y={39.4} size={11} weight={300} tone="band">MULTICLASS SHEET</Txt>
      <Html x={351.1} y={67} w={232} h={32} size={8}>
        When you multiclass, take one of the {cls.name.toLowerCase()} foundation cards and place in your loadout. Then choose one domain to expand into.
      </Html>
      {domains.map((d, k) => (
        <g key={d}>
          <Html x={377 + k * 100.5} y={96} w={22} h={22} className="cs-domain-icon"><DomainIcon domain={d} /></Html>
          <Txt x={401.9 + k * 100.8} y={113.9} size={11} weight={300}>{d.toUpperCase()}</Txt>
          <rect className={multi.domain === d ? 's-band' : 's-paper o-frame'} x={403.7 + k * 115} y={123.8} width={16.8} height={16.8} rx={1.5} />
        </g>
      ))}
      <Html x={346} y={154.5} w={232} h={22} size={8} className="cs-center"><strong>When leveling up, you can take domain cards from this domain at half your level or lower (rounded up).</strong></Html>
      <line className="o-ink" x1={348.4} y1={183.7} x2={576.1} y2={183.7} />
      <Txt x={348.3} y={206.3} size={11} weight={900} tone="title">MULTICLASS FEATURE</Txt>
      <Html x={348.4} y={212.5} w={228} h={132} size={8} className="cs-features" fit>
        {classFeatures(cls).map((f, i) => <ClassFeatureBlock key={f.name ?? i} feature={f} c={c} onChoose={onChoose} />)}
      </Html>
      <Txt x={348.3} y={358.1} size={11} weight={900} tone="title">NOTES</Txt>
      {Array.from({ length: 18 }, (_, i) => <WriteLine key={i} x0={346.3} x1={575.6} y={379.3 + i * 22.68} tone="faint" />)}
      <PageFooter x={14} hf={cls.source === 'HF'} />
    </SheetSvg>
  );
}
